import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { formatDateTimeOrdinal } from './date_util';

const ITEMS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

export const Issues = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const logoutUser = () => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.setItem('currentUser', null);
    window.location = "/";
  };

  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/apiissues', { credentials: 'include' });
      const data = await response.json();
      if (data && data.sessionExpired) { logoutUser(); return; }
      const sorted = sortIssues(data || []);
      setIssues(sorted);
      setFilteredIssues(sorted);
    } catch (err) {
      console.error('Error fetching issues:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const sortIssues = (list) => {
    return [...list].sort((a, b) => {
      const dateA = a && a[1] ? new Date(a[1].value) : new Date(0);
      const dateB = b && b[1] ? new Date(b[1].value) : new Date(0);
      return dateB - dateA;
    });
  };

  const handleChangeInput = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    if (!term) {
      setFilteredIssues(issues);
    } else {
      const filtered = issues.filter(issue => {
        const issueType = issue && issue[6] ? issue[6].value : '';
        const additionalInfo = issue && issue[7] ? issue[7].value : '';
        const contact = issue && issue[8] ? issue[8].value : '';
        const location = issue && issue[10] ? issue[10].value : '';
        return issueType.toLowerCase().includes(term) ||
          additionalInfo.toLowerCase().includes(term) ||
          contact.toLowerCase().includes(term) ||
          location.toLowerCase().includes(term);
      });
      setFilteredIssues(filtered);
    }
    setCurrentPage(1);
  };

  const formatDate = formatDateTimeOrdinal;

  // Pagination
  const totalPages = Math.ceil(filteredIssues.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedIssues = filteredIssues.slice(startIndex, endIndex);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredIssues.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredIssues, currentPage]);

  const headers = (
    <tr>
      <th>Date Submitted</th>
      <th>Issue Type</th>
      <th>Additional Information</th>
    </tr>
  );

  return (
    <div className="tickets">
      <div className="ticketsList">
        <h2>Issues</h2>
        <div className="ticketsActionsWrapper">
          <div className="ticketsInputWrapper">
            <input placeholder="Search issues..." onChange={handleChangeInput} />
          </div>
          <button className="mainButton" onClick={() => navigate('/issues/new')}>+ Submit a New Issue</button>
        </div>
        {isLoading ? (
          <div className="skeleton-wrapper">
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px' }}>
              Loading issues...
            </p>
            <table className="skeleton-table">
              <tbody>
                {headers}
                {Array.from({ length: 10 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-medium"></div></td>
                    <td className="tableCell"><div className="skeleton-cell skeleton-cell-long"></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px' }}>
              Showing {filteredIssues.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredIssues.length)} of {filteredIssues.length} issues
            </p>
            <table id="tableWrapper">
              <tbody>
                {headers}
                {paginatedIssues.length > 0 ? paginatedIssues.map((issue, idx) => (
                  <tr key={idx} className="tableRow" onClick={() => navigate(`/issue/${issue[3] ? issue[3].value : ''}`)} style={{ cursor: 'pointer' }}>
                    <td className="tableCell">{issue && issue[1] ? formatDate(issue[1].value) : ''}</td>
                    <td className="tableCell">{issue && issue[6] ? issue[6].value : ''}</td>
                    <td className="tableCell">{issue && issue[7] ? issue[7].value : ''}</td>
                  </tr>
                )) : (
                  <tr>
                    <td className="tableCell" colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                      No issues found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <button
                  className={`pagination-btn ${currentPage === 1 ? 'pagination-btn--disabled' : ''}`}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                {getPageNumbers(currentPage, totalPages).map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'pagination-btn--active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                ))}
                <button
                  className={`pagination-btn ${currentPage === totalPages ? 'pagination-btn--disabled' : ''}`}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
